import React, { useState } from 'react';
import { 
  CreditCardIcon, 
  UserIcon, 
  CalendarIcon, 
  LockClosedIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  GlobeAltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { RadioGroup } from '@headlessui/react';
import visaLogo from '../../../assets/images/visa-logo.png';
import mastercardLogo from '../../../assets/images/mastercard-logo.png';
import amexLogo from '../../../assets/images/amex-logo.png';
import mpesaLogo from '../../../assets/images/mpesa-logo.png';

const PaymentForm = ({ formik, isLoading, bookingData }) => {
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  
  // Credit card type detection based on card number
  const detectCardType = (cardNumber) => {
    const visaRegex = /^4/;
    const mastercardRegex = /^5[1-5]/;
    const amexRegex = /^3[47]/;
    
    if (visaRegex.test(cardNumber)) return 'visa';
    if (mastercardRegex.test(cardNumber)) return 'mastercard';
    if (amexRegex.test(cardNumber)) return 'amex';
    return null;
  };
  
  const cardType = detectCardType(formik.values.cardNumber);
  
  // Format card number with spaces
  const formatCardNumber = (value) => {
    if (!value) return value;
    
    // Remove all non-digit characters
    const v = value.replace(/\D/g, '');
    
    // Different formatting for different card types
    if (cardType === 'amex') {
      // XXXX XXXXXX XXXXX
      return v.substring(0, 4) + (v.length > 4 ? ' ' + v.substring(4, 10) + (v.length > 10 ? ' ' + v.substring(10, 15) : '') : '');
    } else {
      // XXXX XXXX XXXX XXXX
      return v.substring(0, 4) + (v.length > 4 ? ' ' + v.substring(4, 8) + (v.length > 8 ? ' ' + v.substring(8, 12) + (v.length > 12 ? ' ' + v.substring(12, 16) : '') : '') : '');
    }
  };
  
  // Handle card number input
  const handleCardNumberChange = (e) => {
    const input = e.target.value.replace(/\D/g, '').substring(0, 16);
    formik.setFieldValue('cardNumber', input);
  };
  
  // Handle expiry date input
  const handleExpiryDateChange = (e) => {
    let input = e.target.value.replace(/\D/g, '');
    
    if (input.length > 2) {
      input = input.substring(0, 2) + '/' + input.substring(2, 4);
    }
    
    formik.setFieldValue('expiryDate', input);
  };
  
  // Handle CVV input
  const handleCvvChange = (e) => {
    const input = e.target.value.replace(/\D/g, '').substring(0, 4);
    formik.setFieldValue('cvv', input);
  };
  
  // Payment methods
  const paymentMethods = [
    {
      id: 'credit_card',
      title: 'Credit Card',
      description: 'Pay securely with your credit card',
      icon: <CreditCardIcon className="h-6 w-6" />,
      logos: (
        <div className="flex space-x-2">
          <img src={visaLogo} alt="Visa" className="h-8" />
          <img src={mastercardLogo} alt="Mastercard" className="h-8" />
          <img src={amexLogo} alt="American Express" className="h-8" />
        </div>
      )
    },
    {
      id: 'mpesa',
      title: 'M-Pesa',
      description: 'Pay with M-Pesa mobile money',
      icon: <img src={mpesaLogo} alt="M-Pesa" className="h-6 w-6" />,
      logos: null
    }
  ];
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h2 className="text-xl font-bold mb-6">Payment Information</h2>
      
      {/* Payment Method Selection */}
      <div className="mb-6">
        <RadioGroup value={paymentMethod} onChange={setPaymentMethod}>
          <RadioGroup.Label className="block text-sm font-medium text-gray-700 mb-3">
            Select Payment Method
          </RadioGroup.Label>
          <div className="space-y-4">
            {paymentMethods.map((method) => (
              <RadioGroup.Option
                key={method.id}
                value={method.id}
                className={({ checked }) => `
                  ${checked ? 'border-primary bg-primary-50' : 'border-gray-300'}
                  relative border rounded-lg p-4 flex cursor-pointer focus:outline-none
                `}
              >
                {({ checked }) => (
                  <>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <div className={`
                          ${checked ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'}
                          rounded-full p-2 mr-4
                        `}>
                          {method.icon}
                        </div>
                        <div>
                          <RadioGroup.Label
                            as="p"
                            className={`font-medium ${checked ? 'text-primary' : 'text-gray-700'}`}
                          >
                            {method.title}
                          </RadioGroup.Label>
                          <RadioGroup.Description
                            as="p"
                            className="text-sm text-gray-500"
                          >
                            {method.description}
                          </RadioGroup.Description>
                        </div>
                      </div>
                      {method.logos}
                    </div>
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>
      
      <form onSubmit={formik.handleSubmit}>
        {paymentMethod === 'credit_card' && (
          <>
            {/* Card Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Card Details</h3>
              
              {/* Card Name */}
              <div className="mb-4">
                <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1">
                  Name on Card
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="cardName"
                    name="cardName"
                    value={formik.values.cardName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      formik.touched.cardName && formik.errors.cardName
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                    placeholder="John Doe"
                  />
                </div>
                {formik.touched.cardName && formik.errors.cardName && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.cardName}</p>
                )}
              </div>
              
              {/* Card Number */}
              <div className="mb-4">
                <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Card Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCardIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="cardNumber"
                    name="cardNumber"
                    value={formatCardNumber(formik.values.cardNumber)}
                    onChange={handleCardNumberChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full pl-10 pr-10 py-2 border ${
                      formik.touched.cardNumber && formik.errors.cardNumber
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                    placeholder="1234 5678 9012 3456"
                  />
                  {cardType && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <img 
                        src={
                          cardType === 'visa' ? visaLogo :
                          cardType === 'mastercard' ? mastercardLogo :
                          cardType === 'amex' ? amexLogo : null
                        } 
                        alt={cardType} 
                        className="h-6" 
                      />
                    </div>
                  )}
                </div>
                {formik.touched.cardNumber && formik.errors.cardNumber && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.cardNumber}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Expiry Date */}
                <div>
                  <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="expiryDate"
                      name="expiryDate"
                      value={formik.values.expiryDate}
                      onChange={handleExpiryDateChange}
                      onBlur={formik.handleBlur}
                      className={`block w-full pl-10 pr-3 py-2 border ${
                        formik.touched.expiryDate && formik.errors.expiryDate
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                      placeholder="MM/YY"
                    />
                  </div>
                  {formik.touched.expiryDate && formik.errors.expiryDate && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.expiryDate}</p>
                  )}
                </div>
                
                {/* CVV */}
                <div>
                  <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
                    CVV
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="cvv"
                      name="cvv"
                      value={formik.values.cvv}
                      onChange={handleCvvChange}
                      onBlur={formik.handleBlur}
                      className={`block w-full pl-10 pr-3 py-2 border ${
                        formik.touched.cvv && formik.errors.cvv
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                      placeholder={cardType === 'amex' ? '4 digits' : '3 digits'}
                    />
                  </div>
                  {formik.touched.cvv && formik.errors.cvv && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.cvv}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Billing Address */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Billing Address</h3>
              
              {/* Address */}
              <div className="mb-4">
                <label htmlFor="billingAddress" className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="billingAddress"
                    name="billingAddress"
                    value={formik.values.billingAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      formik.touched.billingAddress && formik.errors.billingAddress
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                    placeholder="123 Main St, Apt 4B"
                  />
                </div>
                {formik.touched.billingAddress && formik.errors.billingAddress && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.billingAddress}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* City */}
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`block w-full pl-10 pr-3 py-2 border ${
                        formik.touched.city && formik.errors.city
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                      placeholder="New York"
                    />
                  </div>
                  {formik.touched.city && formik.errors.city && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.city}</p>
                  )}
                </div>
                
                {/* ZIP Code */}
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formik.values.zipCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full px-3 py-2 border ${
                      formik.touched.zipCode && formik.errors.zipCode
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                    placeholder="10001"
                  />
                  {formik.touched.zipCode && formik.errors.zipCode && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.zipCode}</p>
                  )}
                </div>
              </div>
              
              {/* Country */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="country"
                    name="country"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`block w-full pl-10 pr-3 py-2 border ${
                      formik.touched.country && formik.errors.country
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                  >
                    <option value="">Select Country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="KE">Kenya</option>
                    <option value="TZ">Tanzania</option>
                    <option value="UG">Uganda</option>
                    <option value="RW">Rwanda</option>
                    <option value="ZA">South Africa</option>
                  </select>
                </div>
                {formik.touched.country && formik.errors.country && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.country}</p>
                )}
              </div>
            </div>
          </>
        )}
        
        {paymentMethod === 'mpesa' && (
          <div className="mb-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">M-Pesa Payment Instructions</h3>
              <p className="text-yellow-700 mb-2">
                To complete your payment using M-Pesa:
              </p>
              <ol className="list-decimal list-inside text-yellow-700 space-y-1">
                <li>Go to M-Pesa on your phone</li>
                <li>Select "Lipa na M-Pesa"</li>
                <li>Select "Pay Bill"</li>
                <li>Enter Business Number: <span className="font-semibold">123456</span></li>
                <li>Enter Account Number: <span className="font-semibold">KT-{bookingData?.id || '12345'}</span></li>
                <li>Enter Amount: <span className="font-semibold">${bookingData?.totalPrice}</span></li>
                <li>Enter your M-Pesa PIN</li>
                <li>You will receive a confirmation SMS</li>
              </ol>
              <p className="text-yellow-700 mt-2">
                After completing the payment, click the "Confirm Payment" button below.
              </p>
            </div>
            
            <div className="mb-4">
              <label htmlFor="mpesaCode" className="block text-sm font-medium text-gray-700 mb-1">
                M-Pesa Transaction Code
              </label>
              <input
                type="text"
                id="mpesaCode"
                name="mpesaCode"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="e.g. QWE1234XYZ"
              />
              <p className="mt-1 text-sm text-gray-500">
                Enter the transaction code from your M-Pesa confirmation message
              </p>
            </div>
          </div>
        )}
        
        {/* Security Note */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-start">
          <ShieldCheckIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-gray-800 mb-1">Secure Payment</h4>
            <p className="text-sm text-gray-600">
              Your payment information is encrypted and securely processed. We do not store your full card details.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-md transition-colors duration-300"
          >
            {isLoading ? 'Processing...' : `Pay $${bookingData?.totalPrice}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
